/* eslint-disable no-sequences */
import React, { FC, useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { push as Push } from 'redux-first-history';
import { parse } from 'query-string';

import styled from 'styled-components';
import { isEmpty, isNil, truncate, map } from 'lodash-es';
import { Box } from '@material-ui/core';

import {
    CustomTable,
    CustomTableHeadersProps,
    CustomTableRowsProps,
    Container,
    TabsPanel,
    Paper,
    Typography,
    Pagination,
    MobileTable,
    ContentLoader,
    MobileCardRowsProps,
} from '@dev/ee-components';
import { safelyDecodeBase64Url } from '@dev/ee-tools';
import { CampaignType, TemplateLink } from '@dev/ee-api/ee-api-models';
import { useParams } from 'react-router-dom';
import { calculateActivePage } from '@/services/tools/handleUrlParams';
import { PickedCampaignInterface } from '@/store/campaigns/types';

import useMobile from '@/services/hooks/useMobile';
import { ApplicationState } from '@/store';
import { GetCampaignByName } from '@/store/campaigns/actions';
import { GetLinkTrackingAbSplitStart } from '@/store/reports/actions';
import { CampaignsPaths } from '@/services/dictionaries/Paths';
import { AbSplitLinksProps } from './AbSplitLinksInterface';

const LinkColumn = styled.div`
    padding: 20px 0;
`;

const LinkTemplatesSection = styled.div`
    padding-top: 16px;
`;

const LinkColumnMobile = styled.div`
    overflow-wrap: break-word;
    white-space: break-spaces;
`;

const LinksTable = (props: AbSplitLinksProps): JSX.Element => {
    const { t } = useTranslation();

    const {
        match: { params },
    } = props;
    const decodedName = safelyDecodeBase64Url(params.name);
    const campaignType = params.type;
    const dispatch = useDispatch();
    const { pickedCampaign } = useSelector((state: ApplicationState) => state.campaigns);
    useEffect(() => {
        dispatch(GetCampaignByName({ name: decodedName, campaignType }));
    }, []);
    const { collection, loading, totalCount, offset, limit } = useSelector(
        (state: ApplicationState) => state.reports.linkTrackingAbSplit
    );
    const activePage = calculateActivePage(offset, limit);

    const linksHeaders: CustomTableHeadersProps[] = [
        {
            id: 'link',
            label: t('link'),
        },
        {
            id: 'uniqueclicks',
            label: t('uniqueclicks'),
            columnAlign: 'right',
            headerAlign: 'right',
        },
        {
            id: 'clicks',
            label: t('clicks'),
            columnAlign: 'right',
            headerAlign: 'right',
        },
    ];

    const columnsWidth = {
        link: '70%',
        uniqueclicks: '15%',
        clicks: '15%',
    };

    const changePage = (page: number, perPage: number): void => {
        dispatch(
            GetLinkTrackingAbSplitStart({
                channelName: decodedName,
                activePage: page,
                perPage,
                isUpdateUrlRequired: true,
            })
        );
    };

    return (
        <Paper padding={{ top: 24, bottom: 24 }}>
            <CustomTable
                headers={linksHeaders}
                rows={getLinksRow({ t, collection, pickedCampaign })}
                isEmpty={isEmpty(collection) && !loading}
                loading={loading}
                stickyHeader={true}
                rowPadding={48}
                rowHeight={60}
                hideCheckboxes={true}
                columnsWidth={columnsWidth}
                emptyStateProps={{ text: t('emptystate') }}
                disableRowClick={true}
                disableFirstRowBorderTop={true}
            />
            <Pagination
                dataLength={totalCount}
                perPage={limit}
                activePage={activePage}
                handlePaginationChange={changePage}
            />
        </Paper>
    );
};

const getLinksRow = (props: {
    t: TFunction;
    collection: TemplateLink[];
    pickedCampaign: PickedCampaignInterface;
}): CustomTableRowsProps[] => {
    const { t, collection, pickedCampaign } = props;
    const { templateLabels } = pickedCampaign;

    if (isEmpty(collection) || !templateLabels) {
        return [];
    }

    return map(collection, (link) => ({
        link: (
            <LinkColumn>
                {truncate(link.Link, { length: 70 })}
                <LinkTemplatesSection>
                    {map(link?.Stats, (stat, i) => {
                        const key = templateLabels[stat.Template];
                        return (
                            <Typography
                                key={i}
                                variant="body1"
                                bodyVariant="help"
                                style={{ textTransform: 'capitalize' }}
                            >
                                {`${t('version')} ${key}`}
                            </Typography>
                        );
                    })}
                </LinkTemplatesSection>
            </LinkColumn>
        ),
        uniqueclicks: (
            <>
                {link.Summary.UniqueClicks}
                <LinkTemplatesSection>
                    {map(link?.Stats, (stat, j) => (
                        <Typography key={`${j}-uniqueclicks`} variant="body1" bodyVariant="help">
                            {stat.UniqueClicks}
                        </Typography>
                    ))}
                </LinkTemplatesSection>
            </>
        ),
        clicks: (
            <>
                {link?.Summary.Clicks}
                <LinkTemplatesSection>
                    {map(link.Stats, (stat, k) => (
                        <Typography key={`${k}-clicks`} variant="body1" bodyVariant="help">
                            {stat.Clicks}
                        </Typography>
                    ))}
                </LinkTemplatesSection>
            </>
        ),
    }));
};

const useGetLinksRowMobile = (
    collection: TemplateLink[],
    pickedCampaign: PickedCampaignInterface,
    loading: boolean
): MobileCardRowsProps[] => {
    const { templateLabels } = pickedCampaign;

    if (loading) {
        return [
            {
                tableTitle: <ContentLoader count={1} mode="row" />,
                uniqueclicks: <ContentLoader count={1} mode="row" />,
                clicks: <ContentLoader count={1} mode="row" />,
            },
        ];
    }

    if (!loading && (isEmpty(collection) || !templateLabels)) {
        return [];
    }

    return map(collection, (link) => ({
        tableTitle: (
            <LinkColumnMobile>
                <Typography variant="body1" bodyVariant="onPaper">
                    {link.Link}
                </Typography>
            </LinkColumnMobile>
        ),
        uniqueclicks: (
            <>
                <Typography variant="body1" bodyVariant="onPaper">
                    {link.Summary.UniqueClicks}
                </Typography>
                <LinkTemplatesSection>
                    {map(link?.Stats, (stat, i) => (
                        <Typography key={i} variant="body1" bodyVariant="onPaper">
                            {stat.UniqueClicks}
                        </Typography>
                    ))}
                </LinkTemplatesSection>
            </>
        ),
        clicks: (
            <>
                <Typography variant="body1" bodyVariant="onPaper">
                    {link?.Summary.Clicks}
                </Typography>
                <LinkTemplatesSection>
                    {map(link.Stats, (stat, i) => (
                        <Typography key={i} variant="body1" bodyVariant="onPaper">
                            {stat.Clicks}
                        </Typography>
                    ))}
                </LinkTemplatesSection>
            </>
        ),
    }));
};

const linksHeadersMobile = (
    pickedCampaign: PickedCampaignInterface,
    links: TemplateLink[],
    t
): string[] | JSX.Element[] => {
    const { templateLabels } = pickedCampaign;

    if (isEmpty(links) || !templateLabels) {
        return [];
    }

    return map(
        links,
        (link) => (
            (
                <>
                    <Typography variant="body1" bodyVariant="help">
                        {t('uniqueclicks')}
                    </Typography>
                    <LinkTemplatesSection>
                        {map(link?.Stats, (stat, j) => {
                            const key = templateLabels[stat.Template];
                            return (
                                <Typography key={j} variant="body1" bodyVariant="help">
                                    {t('version')} {key}
                                </Typography>
                            );
                        })}
                    </LinkTemplatesSection>
                </>
            ),
            (
                <>
                    <Typography variant="body1" bodyVariant="help">
                        {t('clicks')}
                    </Typography>
                    <LinkTemplatesSection>
                        {map(link?.Stats, (stat, k) => {
                            const key = templateLabels[stat.Template];
                            return (
                                <Typography key={k} variant="body1" bodyVariant="help">
                                    {t('version')} {key}
                                </Typography>
                            );
                        })}
                    </LinkTemplatesSection>
                </>
            )
        )
    );
};

const LinksTableMobile = (props: AbSplitLinksProps): JSX.Element => {
    const { t } = useTranslation();

    const {
        match: { params },
    } = props;
    const decodedNameMobile = safelyDecodeBase64Url(params.name);
    const campaignType = params.type;
    const dispatch = useDispatch();
    const { pickedCampaign } = useSelector((state: ApplicationState) => state.campaigns);
    useEffect(() => {
        dispatch(GetCampaignByName({ name: decodedNameMobile, campaignType }));
    }, []);
    const { collection, totalCount, offset, limit, loading } = useSelector(
        (state: ApplicationState) => state.reports.linkTrackingAbSplit
    );
    const activePage = calculateActivePage(offset, limit);

    const changeMobilePage = (page: number, perPage: number): void => {
        dispatch(
            GetLinkTrackingAbSplitStart({
                channelName: decodedNameMobile,
                activePage: page,
                perPage,
                isUpdateUrlRequired: true,
            })
        );
    };

    return (
        <div>
            <MobileTable
                headers={linksHeadersMobile(pickedCampaign, collection, t)}
                rows={useGetLinksRowMobile(collection, pickedCampaign, loading)}
                disableSimplePopover
            />
            <Paper>
                <Pagination
                    dataLength={totalCount}
                    perPage={limit}
                    activePage={activePage}
                    handlePaginationChange={changeMobilePage}
                />
            </Paper>
        </div>
    );
};

const CampaignReportAbSplitLinks: FC<AbSplitLinksProps> = (props: AbSplitLinksProps): JSX.Element => {
    const { name, type } = useParams<{ name: string; type: string }>();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const decodedName = safelyDecodeBase64Url(name);
    const isMobile = useMobile();

    const query = parse(location.search, { arrayFormat: 'comma' });
    const { page, perPage } = query;

    const queryParams = {
        ...(isNil(perPage) ? {} : { perPage: Number(perPage) }),
        ...(isNil(page) ? {} : { activePage: Number(page) }),
    };

    useEffect(() => {
        dispatch(
            GetLinkTrackingAbSplitStart({
                ...queryParams,
                channelName: decodedName,
                isUpdateUrlRequired: true,
            })
        );
    }, []);

    return (
        <Container>
            <TabsPanel
                breadcrumbs={safelyDecodeBase64Url(name)}
                breadcrumbsOnClick={() => {
                    dispatch(
                        Push(
                            (type === CampaignType.AbSplit
                                ? CampaignsPaths.CampaignAbResults
                                : CampaignsPaths.CampaignOverview
                            )
                                .replace(':type', type)
                                .replace(':name', name)
                        )
                    );
                }}
                title={t('links')}
            />
            {!isMobile && <Box paddingTop="32px">{LinksTable(props)}</Box>}
            {isMobile && <Box paddingTop="16px">{LinksTableMobile(props)}</Box>}
        </Container>
    );
};

export default CampaignReportAbSplitLinks;
